import { useState } from 'react';

export interface NavigationProps {
  className?: string;
}

interface NavigationItem {
  text: string;
  href: string;
  title?: string;
  children?: NavigationItem[];
}

export function Navigation({ className = "" }: NavigationProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const navigationItems: NavigationItem[] = [
    {
      text: "Flight Training",
      href: "/flight-training", 
      title: "Flight Training",
      children: [
        { text: "Training", href: "/flight-training/training-process" },
        { text: "Discovery Flight", href: "/flight-training/introductory-flight-lessons" },
        { text: "Flight Instructors", href: "/flight-training/flight-instructors" },
        { text: "Frequently Asked Questions", href: "/flight-training/faqs" },
        { text: "Training Videos", href: "/training-videos" },
        { text: "Milestones", href: "/flight-training/milestones" },
        { text: "Resources & Links", href: "/flight-training/resources-links" },
        { text: "Aviation Medical Examiners", href: "/flight-training/aviation-medical-examiners" },
        { text: "Designated Pilot Examiner Contact Information", href: "/flight-training/designated-pilot-examiner-contact-information" },
        { text: "Cancellation Policy", href: "/flight-training/cancellation-policy" }
      ]
    },
    {
      text: "Go Flying",
      href: "/go-flying",
      children: [
        { text: "Aircraft Rentals", href: "/go-flying/aircraft-rentals" },
        { text: "Restaurant Finder", href: "/go-flying/restaurant-finder" },
        { text: "Current Conditions", href: "/go-flying/current-conditions" },
        { text: "Local Weather", href: "https://rainwise.net/weather/7b2" },
        { text: "Rental Policy", href: "/go-flying/rental-policy" }
      ]
    },
    {
      text: "Maintenance", 
      href: "/maintenance"
    },
    {
      text: "About",
      href: "/about"
    },
    {
      text: "Community Outreach",
      href: "/community-outreach/community-outreach-2"
    },
    {
      text: "Shop",
      href: "http://www.northamptonairportmerchandise.com/shop"
    }
  ];

  return (
    <nav className={className}>
      <ul className="flex items-center gap-8">
        {navigationItems.map((item) => (
          <li
            key={item.text}
            className="relative"
            onMouseEnter={() => item.children && setOpenDropdown(item.text)}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <a
              href={item.href}
              title={item.title}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium flex items-center gap-1"
              {...(item.href.startsWith('http') && {
                target: "_blank",
                rel: "noopener noreferrer"
              })}
            >
              <span>{item.text}</span>
              {item.children && (
                <svg
                  className="w-4 h-4 ml-1 transform transition-transform duration-200"
                  style={{
                    transform: openDropdown === item.text ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </a>
            
            {item.children && openDropdown === item.text && (
              <div className="absolute top-full left-0 z-50 mt-2 w-80 bg-white shadow-lg border border-gray-200 rounded-md py-2">
                <ul className="space-y-1">
                  {item.children.map((child) => (
                    <li key={child.text}>
                      <a
                        href={child.href}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-200"
                        {...(child.href.startsWith('http') && {
                          target: "_blank", 
                          rel: "noopener noreferrer"
                        })}
                      >
                        <span>{child.text}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
        <li>
          <button 
            type="button"
            title="Search"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 p-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="sr-only">Search</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}